/**
 * React Query hooks for Agents
 * 
 * Provides data fetching and mutations for agent management.
 * All queries are scoped to the current tenant.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient, { type ApiResponse, type PaginatedResponse } from '@/lib/api-client';
import { useTenant } from '@/contexts/TenantContext';
import type { Agent, AgentCreate, AgentUpdate, RestrictedAgent } from '../types';

const AGENTS_KEY = 'agents';

export interface AgentsListParams {
  search?: string;
  status?: string;
  page?: number;
  pageSize?: number;
}

/**
 * Fetch agents for the current tenant (ISS-580: server-side search + pagination).
 *
 * Returns the agents array as `data` (backward compatible) and pagination
 * (`total`/`page`/`pageSize`/`totalPages`) — served by the SAME list request,
 * no extra network call. Without params behaves exactly as before (page 1,
 * 20 items).
 *
 * ISS-803: `meta.plan_usage` is intentionally NOT exposed to the agents list:
 * plans do not cap consumption (boosters are billed per pricing); the real
 * usage remains visible in «Mi licencia» (REQ-BIL-018) and is accounted and
 * billed by the backend.
 */
export function useAgents(params: AgentsListParams = {}) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;
  const { search, status, page = 1, pageSize = 20 } = params;

  const query = useQuery({
    queryKey: [AGENTS_KEY, tenantId, search ?? '', status ?? 'all', page, pageSize],
    queryFn: async () => {
      if (!tenantId) {
        throw new Error('No tenant selected');
      }
      const qs = new URLSearchParams();
      if (search) qs.set('search', search);
      if (status && status !== 'all') qs.set('status', status);
      qs.set('page', String(page));
      qs.set('page_size', String(pageSize));
      const { data } = await apiClient.get<PaginatedResponse<Agent>>(
        `/api/v1/tenants/${tenantId}/agents?${qs.toString()}`,
      );
      return data;
    },
    enabled: !!tenantId,
  });

  const meta = query.data?.meta;
  return {
    ...query,
    data: query.data?.data ?? [],
    total: meta?.total ?? query.data?.data?.length ?? 0,
    page: meta?.page ?? page,
    pageSize: meta?.page_size ?? pageSize,
    totalPages: meta?.total_pages ?? 1,
  };
}

/**
 * Fetch single agent by ID
 *
 * Sprint 219 — REQ-AGT-003: `use_only` agents return the restricted
 * representation (identity only) from the API.
 */
export function useAgent(id: string) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: [AGENTS_KEY, tenantId, id],
    queryFn: async () => {
      if (!tenantId) {
        throw new Error('No tenant selected');
      }
      const { data } = await apiClient.get<ApiResponse<Agent | RestrictedAgent>>(
        `/api/v1/tenants/${tenantId}/agents/${id}`,
      );
      return data.data;
    },
    enabled: !!id && !!tenantId,
  });
}

/**
 * Create new agent
 */
export function useCreateAgent() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();

  return useMutation({
    mutationFn: async (agentData: AgentCreate) => {
      const tenantId = currentTenant?.id;
      if (!tenantId) throw new Error('No tenant selected');
      const { data } = await apiClient.post<ApiResponse<Agent>>(
        `/api/v1/tenants/${tenantId}/agents`,
        agentData,
      );
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [AGENTS_KEY] });
    },
  });
}

/**
 * Update existing agent
 */
export function useUpdateAgent(id: string) {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();

  return useMutation({
    mutationFn: async (agentData: AgentUpdate) => {
      const tenantId = currentTenant?.id;
      if (!tenantId) throw new Error('No tenant selected');
      const { data } = await apiClient.patch<ApiResponse<Agent>>(
        `/api/v1/tenants/${tenantId}/agents/${id}`,
        agentData,
      );
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [AGENTS_KEY] });
      queryClient.invalidateQueries({ queryKey: [AGENTS_KEY, id] });
    },
  });
}

/**
 * Delete agent
 */
export function useDeleteAgent() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();

  return useMutation({
    mutationFn: async (id: string) => {
      const tenantId = currentTenant?.id;
      if (!tenantId) throw new Error('No tenant selected');
      await apiClient.delete(`/api/v1/tenants/${tenantId}/agents/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [AGENTS_KEY] });
    },
  });
}
